const CARTOGRAPHER_DEBUG_FLAG_KEY = 'cartographer:debug';
const CARTOGRAPHER_DEBUG_MAX_EVENTS = 400;

type CartographerDebugEvent = {
  ts: string;
  event: string;
  data?: Record<string, unknown>;
};

declare global {
  interface Window {
    __cartographerDebugEvents?: CartographerDebugEvent[];
    __cartographerDebugDump?: () => string;
    __cartographerDebugClear?: () => void;
    __cartographerDebugCopy?: () => Promise<string>;
  }
}

export function isCartographerDebugEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;

  try {
    if (window.localStorage.getItem(CARTOGRAPHER_DEBUG_FLAG_KEY) === '1') {
      return true;
    }
  } catch {
    // Ignore storage access failures.
  }

  try {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('cartographerDebug') === '1';
  } catch {
    return false;
  }
}

export function stringifyDebugReason(reason: unknown): string {
  if (reason instanceof Error) {
    return `${reason.name}: ${reason.message}`;
  }
  if (typeof reason === 'string') return reason;

  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
}

export function pushCartographerDebugEvent(
  event: string,
  data?: Record<string, unknown>,
) {
  if (!isCartographerDebugEnabled() || typeof window === 'undefined') return;

  const entry: CartographerDebugEvent = {
    ts: new Date().toISOString(),
    event,
    data,
  };
  const events = window.__cartographerDebugEvents ?? [];
  events.push(entry);
  if (events.length > CARTOGRAPHER_DEBUG_MAX_EVENTS) {
    events.splice(0, events.length - CARTOGRAPHER_DEBUG_MAX_EVENTS);
  }
  window.__cartographerDebugEvents = events;
  window.__cartographerDebugDump = () =>
    JSON.stringify(window.__cartographerDebugEvents ?? [], null, 2);
  window.__cartographerDebugClear = () => {
    window.__cartographerDebugEvents = [];
  };
  window.__cartographerDebugCopy = async () => {
    const dump = window.__cartographerDebugDump?.() ?? '[]';
    await navigator.clipboard.writeText(dump);
    return dump;
  };

  console.log('[CartographerDebug]', event, data ?? {});
}
